import { useNavigate } from "react-router-dom";
import { ChangeEvent, useCallback, useMemo } from "react";

import useAuth from "$hooks/useAuth";
import { editProduct } from "$api/productApi";
import { PreparationOrderStatus } from "$types";
import { OrderProduct } from "$types/EDBIntegratedOrders";
import { queryClient } from "$utils/queryClient";
import { ProfileEDBHandlersTypes } from "../types/profileEDB.types";

export default function useProfileEDBHandlers({
  edb,
  integratedOrders,
  notEditableProducts,
  opList,
  setProductPopin,
  setFormOpen,
  setProduit,
  fetchEdb,
  fetchIntegratedOrders,
  formMethods,
  updateEDBProducts,
  idEdb,
  produit,
  setShowCart,
  setOpenCartModalValidate,
  setIsAddingMaterial,
  setCurrentTab,
  setProductToDeleteList,
  setOpenModalDeleteMultipleProduct,
  setIsModifyingEdb,
  productsQuantityMap,
  setProductsQuantityMap,
}): ProfileEDBHandlersTypes {
  const { token } = useAuth();
  const navigate = useNavigate();

  const resetProductPopin = useCallback(() => {
    setProductPopin(undefined);
  }, [setProductPopin]);

  const isDeliveryEditable = Boolean(
    !opList.find((op: any) => op.status >= PreparationOrderStatus.SHIPPED)
  );

  const hasAPreparationOrderValidated = useMemo(
    () =>
      opList.some((op: any) => op.status >= PreparationOrderStatus.IN_PROGRESS),
    [opList]
  );

  const togglePopin = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        setProduit(null);
      }
      setFormOpen(isOpen);
    },
    [setProduit, setFormOpen]
  );

  const onSubmit = useCallback(
    async (data: any) => {
      try {
        const result: any = await editProduct(
          token,
          { ...data, company: edb?.company?._id || "", isReferenced: false },
          produit?.relatedProduct?._id || undefined
        );
        if (result?.data?.data) {
          // call refresh here
          fetchEdb();
          togglePopin(false);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err);
      }
    },
    [
      fetchEdb,
      togglePopin,
      edb?.company?._id,
      produit?.relatedProduct?._id,
      token,
    ]
  );

  const onValidateCart = useCallback(async () => {
    // Merge integrated orderProducts before sending them
    const addedOrderProducts = formMethods.getValues(
      "integratedOrders.orderProducts"
    );
    const newOrderProducts: OrderProduct[] =
      integratedOrders?.orderProducts || [];
    addedOrderProducts.forEach((op) => {
      const foundIndex = newOrderProducts.findIndex(
        (newOp) =>
          newOp.order.id === op.order.id &&
          newOp.suborderId === op.suborderId &&
          newOp.suborderProductId === op.suborderProductId
      );
      if (foundIndex === -1) {
        newOrderProducts.push(op);
      } else {
        newOrderProducts[foundIndex].quantity += op.quantity;
      }
    });

    const newIntegratedOrders = {
      ...integratedOrders,
      orderProducts: newOrderProducts,
    };
    const newValues = {
      ...formMethods.getValues(),
      integratedOrders: newIntegratedOrders,
    };
    await updateEDBProducts(newValues, edb);
    await queryClient.removeQueries({
      queryKey: ["getEdbById", idEdb],
    });
    await fetchEdb();
    await formMethods.trigger();
    setShowCart(false);
    setOpenCartModalValidate(false);
    setIsAddingMaterial(false);
    formMethods.setValue("integratedOrders.orderProducts", []);
    await fetchIntegratedOrders(idEdb);
  }, [
    edb,
    fetchEdb,
    fetchIntegratedOrders,
    idEdb,
    integratedOrders,
    formMethods,
    updateEDBProducts,
    setIsAddingMaterial,
    setOpenCartModalValidate,
    setShowCart,
  ]);

  const handleChangeTab = useCallback(
    (_: ChangeEvent<any>, newValue: number) => {
      setCurrentTab(newValue);
      navigate(`${window.location.pathname}?tabpane=${newValue}`);
    },
    [navigate, setCurrentTab]
  );

  const getProductInOP = useCallback(
    (edbProductId: string) => {
      return notEditableProducts.reduce((total, product) => {
        if (product.productId === edbProductId) {
          return total + product.qty;
        }
        return total;
      }, 0);
    },
    [notEditableProducts]
  );

  const resetDelete = () => {
    fetchEdb();
    setProductToDeleteList([]);
    setOpenModalDeleteMultipleProduct(false);
    setIsModifyingEdb(false);
    productsQuantityMap.clear();
    setProductsQuantityMap(productsQuantityMap);
  };

  return {
    isDeliveryEditable,
    hasAPreparationOrderValidated,

    onSubmit,
    resetDelete,
    togglePopin,
    getProductInOP,
    onValidateCart,
    handleChangeTab,
    resetProductPopin,
  };
}
